import React, { useState, useCallback, useRef } from 'react';
import { useWorkflow } from '../contexts/WorkflowContext';
import WorkflowNode from './WorkflowNode';
import './Canvas.css';

const Canvas = ({ onShowNodeSettings, onShowOutput, onShowLoading, onHideLoading }) => {
  const { 
    nodes, 
    connections, 
    currentWorkflowId, 
    workflows,
    addNode,
    updateNode,
    deleteNode,
    addConnection,
    executeWorkflow,
    clearCanvas
  } = useWorkflow();

  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [selectedNode, setSelectedNode] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStart, setConnectionStart] = useState(null);

  const canvasRef = useRef(null);
  const canvasContainerRef = useRef(null);

  const currentWorkflow = currentWorkflowId ? workflows[currentWorkflowId] : null;

  // Handle dropping nodes from sidebar
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('Drop event triggered!'); // Debug log
    
    const nodeType = e.dataTransfer.getData('application/json');
    console.log('Node data:', nodeType); // Debug log
    
    if (!nodeType) {
      console.log('No node data found in drop'); // Debug log
      return;
    }

    try {
      const nodeData = JSON.parse(nodeType);
      const rect = canvasContainerRef.current.getBoundingClientRect();
      
      // Calculate position considering zoom and pan
      const x = (e.clientX - rect.left - pan.x) / zoom;
      const y = (e.clientY - rect.top - pan.y) / zoom;

      console.log('Adding node at position:', { x, y, type: nodeData.type }); // Debug log

      // Add the node to the canvas
      addNode(nodeData.type, { x, y }, nodeData.settings || {});
      
      // Visual feedback
      const canvas = canvasContainerRef.current;
      canvas.style.transform = 'scale(1.02)';
      setTimeout(() => {
        canvas.style.transform = 'scale(1)';
      }, 200);
      
    } catch (error) {
      console.error('Error parsing node data:', error);
    }
  }, [addNode, pan, zoom]);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    
    // Add visual feedback during drag over
    if (canvasContainerRef.current) {
      canvasContainerRef.current.style.border = '2px dashed #e50914';
    }
  };

  const handleDragLeave = (e) => {
    // Remove visual feedback when leaving canvas
    if (canvasContainerRef.current && !e.currentTarget.contains(e.relatedTarget)) {
      canvasContainerRef.current.style.border = 'none';
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Handle canvas panning (only when clicking on canvas background)
  const handleMouseDown = (e) => {
    // Only start panning if clicking directly on canvas background, not on nodes
    if ((e.target === canvasContainerRef.current || e.target.classList.contains('canvas-grid')) 
        && !e.target.closest('.workflow-node')) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
      e.target.style.cursor = 'grabbing';
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      const newPan = {
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      };
      setPan(newPan);
    }
  };

  const handleMouseUp = (e) => {
    if (isDragging) {
      setIsDragging(false);
      if (e.target) {
        e.target.style.cursor = 'grab';
      }
    }
  };

  // Handle node interactions
  const handleNodeClick = (node) => {
    setSelectedNode(node);
  };

  const handleNodeSettings = (node) => {
    onShowNodeSettings({ show: true, nodeData: node });
  };

  const handleNodeOutput = (node) => {
    onShowOutput({ show: true, outputData: node.output || { type: 'text', content: 'No output available' } });
  };

  const handleConnectionStart = (e, outputId) => {
    e.stopPropagation();
    setIsConnecting(true);
    setConnectionStart({ nodeId: outputId.nodeId, outputId: outputId.outputId });
  };

  const handleConnectionEnd = (e, inputId) => {
    e.stopPropagation();
    if (isConnecting && connectionStart) {
      addConnection(connectionStart.nodeId, connectionStart.outputId, inputId.nodeId, inputId.inputId);
      setIsConnecting(false);
      setConnectionStart(null);
    }
  };

  // Handle canvas click to deselect nodes
  const handleCanvasClick = (e) => {
    // Only deselect if clicking on canvas background, not on nodes
    if ((e.target === canvasContainerRef.current || e.target.classList.contains('canvas-grid'))
        && !e.target.closest('.workflow-node')) {
      setSelectedNode(null);
    }
  };

  // Zoom controls
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev * 1.2, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev / 1.2, 0.3));
  };

  const handleResetZoom = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // Execute workflow
  const handleExecuteWorkflow = async () => {
    if (!currentWorkflowId) {
      alert('Please select a workflow first');
      return;
    }
    
    onShowLoading('Executing workflow...');
    try {
      await executeWorkflow(currentWorkflowId);
      onHideLoading();
    } catch (error) {
      console.error('Error executing workflow:', error);
      onHideLoading();
      alert('Error executing workflow');
    }
  };

  // Save workflow to localStorage
  const handleSaveWorkflow = () => {
    if (!currentWorkflowId) {
      alert('Please select a workflow first');
      return;
    }

    try {
      const workflowData = {
        workflow: currentWorkflow,
        nodes: nodes,
        connections: connections,
        timestamp: new Date().toISOString()
      };

      const dataStr = JSON.stringify(workflowData);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      
      const link = document.createElement('a');
      link.href = URL.createObjectURL(dataBlob);
      link.download = `${currentWorkflow.name.replace(/\s+/g, '_')}_workflow.json`;
      link.click();
      
      alert('Workflow saved successfully!');
    } catch (error) {
      console.error('Error saving workflow:', error);
      alert('Error saving workflow');
    }
  };

  // Open workflow from file
  const handleOpenWorkflow = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const workflowData = JSON.parse(event.target.result);
            
            // Load the workflow data
            if (workflowData.workflow && workflowData.nodes) {
              // This would need to be implemented in the context
              alert('Workflow loaded successfully! (Note: Full implementation requires context updates)');
            } else {
              alert('Invalid workflow file format');
            }
          } catch (error) {
            console.error('Error parsing workflow file:', error);
            alert('Error loading workflow file');
          }
        };
        reader.readAsText(file);
      }
    };
    
    input.click();
  };

  // Reset canvas
  const handleResetCanvas = () => {
    if (window.confirm('Are you sure you want to reset the canvas? This will clear all nodes.')) {
      clearCanvas();
      setPan({ x: 0, y: 0 });
      setZoom(1);
      setSelectedNode(null);
      alert('Canvas reset successfully!');
    }
  };

  // Clear canvas
  const handleClearCanvas = () => {
    if (window.confirm('Are you sure you want to clear the canvas? This will remove all nodes.')) {
      clearCanvas();
      setSelectedNode(null);
      alert('Canvas cleared successfully!');
    }
  };

  return (
    <div className="canvas-area">
      {/* Toolbar */}
      <div className="toolbar">
        <div className="toolbar-left">
          <button className="btn btn-secondary" onClick={handleSaveWorkflow}>
            <i className="fas fa-save"></i> Save
          </button>
          <button className="btn btn-secondary" onClick={handleOpenWorkflow}>
            <i className="fas fa-folder-open"></i> Open
          </button>
          <button className="btn btn-secondary" onClick={handleResetCanvas}>
            <i className="fas fa-undo"></i> Reset
          </button>
          <button className="btn btn-secondary" onClick={handleClearCanvas}>
            <i className="fas fa-trash"></i> Clear
          </button>
        </div>

        <div className="toolbar-center">
          {currentWorkflow && (
            <div className="workflow-name">
              {currentWorkflow.name}
            </div>
          )}
        </div>

        <div className="toolbar-right">
          <button className="btn btn-primary" onClick={handleExecuteWorkflow}>
            <i className="fas fa-play"></i> Execute
          </button>
        </div>
      </div>

      {/* Canvas */}
      <div
        className="canvas"
        id="canvas"
        ref={canvasRef}
        onClick={handleCanvasClick}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        {/* Large Canvas Container with Grid */}
        <div 
          className="canvas-container"
          ref={canvasContainerRef}
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: '0 0',
            width: '5000px',
            height: '5000px',
            position: 'relative'
          }}
        >
          {/* Background Grid */}
          <div className="canvas-grid"></div>

          {/* Render connections */}
          {connections.map(connection => (
            <svg
              key={`${connection.fromNode}-${connection.fromOutput}-${connection.toNode}-${connection.toInput}`}
              className="connection-line"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none'
              }}
            >
              <line
                x1={connection.fromX || 0}
                y1={connection.fromY || 0}
                x2={connection.toX || 0}
                y2={connection.toY || 0}
                stroke="#e50914"
                strokeWidth="3"
                strokeLinecap="round"
              />
            </svg>
          ))}

          {/* Render nodes */}
          {nodes.map(node => (
            <WorkflowNode
              key={node.id}
              node={node}
              isSelected={selectedNode?.id === node.id}
              onClick={() => handleNodeClick(node)}
              onSettings={() => handleNodeSettings(node)}
              onOutput={() => handleNodeOutput(node)}
              onConnectionStart={handleConnectionStart}
              onConnectionEnd={handleConnectionEnd}
              isConnecting={isConnecting}
              connectionStart={connectionStart}
            />
          ))}

          {/* Canvas Placeholder */}
          {nodes.length === 0 && (
            <div className="canvas-placeholder">
              <i className="fas fa-magic"></i>
              <h3>Welcome to Your Workflow Canvas!</h3>
              <p>Drag nodes from the sidebar to get started</p>
              <p>Connect them to create amazing AI-powered workflows</p>
              <p>Use zoom and pan to navigate large workflows</p>
              <div className="debug-info">
                <p><strong>Debug:</strong> Canvas is ready for drag & drop</p>
                <p>Current zoom: {Math.round(zoom * 100)}%</p>
                <p>Canvas size: 5000x5000px</p>
              </div>
            </div>
          )}
        </div>

        {/* Zoom Controls */}
        <div className="zoom-controls">
          <button className="zoom-btn" onClick={handleZoomOut} title="Zoom Out">
            <i className="fas fa-minus"></i>
          </button>
          <div className="zoom-level">{Math.round(zoom * 100)}%</div>
          <button className="zoom-btn" onClick={handleZoomIn} title="Zoom In">
            <i className="fas fa-plus"></i>
          </button>
          <button className="zoom-btn" onClick={handleResetZoom} title="Reset Zoom">
            <i className="fas fa-home"></i>
          </button>
        </div>

        {/* Canvas Info */}
        <div className="canvas-info">
          <span>Canvas: 5000x5000px</span>
          <span>Zoom: {Math.round(zoom * 100)}%</span>
          <span>Nodes: {nodes.length}</span>
          <span>Connections: {connections.length}</span>
        </div>
      </div>
    </div>
  );
};

export default Canvas;
